import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { RestService } from 'src/app/services/rest.service';
import { detailsAdapter } from './new-details.adapter';
import { DebugService } from 'src/app/services/debug.service';
import { StatisticsService } from 'src/app/services/statistics.service';
import { AppDataService } from "src/app/services/appData.service";
import { TranslationService } from 'src/app/services/translation.service';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root'})

export class DetailsService {
    private http = inject(HttpClient);
    private rest = inject(RestService);
    private appData = inject(AppDataService);
    private stats = inject(StatisticsService);
    private debugService = inject(DebugService);
    private _translationService = inject(TranslationService);
    private isModal = signal(false);
    private pointsData: any = [];
    private eventsData: any = [];
    private hikingsData: any = [];
    public itemDetails: any = signal(null);
    public gpxData = signal(null);
    private _isLoadingDetailsPage = signal<boolean>(false);
    public isLoadingDetailsPage = this._isLoadingDetailsPage.asReadonly();

    setPointData(item: any){
      this.pointsData[item.id] = item;
    }
    
    setEventData(item: any){
      this.eventsData[item.id] = item;
    }
    

    resetItemDetails(){
      this.itemDetails.set(null)
    }

    getEventDetailsServiceSignal(id: string) {
      if (this.eventsData?.[id]){
        this.debugService.info('Loading event cache data...', '[DetailesService]');
        return of(this.eventsData[id]);
      }

      this._isLoadingDetailsPage.set(true);
      return this.getEventDetails(id).pipe(
        map((response: any) => {
          this.debugService.debug(response, '[DetailesService] Events API');
          return detailsAdapter(response)
        }),
        tap((response: any) => {
          this.debugService.debug(response, '[DetailesService] Events API Mapped');
          this.eventsData[id] = response;
        }),
        finalize(() => this._isLoadingDetailsPage.set(false)))
    }
    
    getEventDetails(id: string): Observable<any> {
      const url = `${this.rest.apiUrlBase}/api/get-event-app-details/${id}`;
      return this.http.get<any>(url).pipe(
        tap((response: any) => {
          if (response.error)
            throw new HttpErrorResponse({error: response.error, status: 404, statusText: 'Not Found'});
        })
      );
    }

    setEvent(item: any){
      this.itemDetails.set(item);
      this.stats.addEvent(item.id);
      this.increaseEventsVisits(item.id);
    }
  
    getPointDetailsServiceSignal(id: string) {
      if (this.pointsData?.[id]){
        this.debugService.info('Loading point cache data...', '[DetailesService]');
        return of(this.pointsData[id]);
      }

      this._isLoadingDetailsPage.set(true);
      return this.getPointDetails(id)
        .pipe(
          map((response: any) => {
            this.debugService.debug(response, '[DetailesService] Point API');
            return detailsAdapter(response)
          }),
          tap((response: any) => {
            this.debugService.debug(response, '[DetailesService] Point API Mapped');
            this.pointsData[id] = response;
          }),
          finalize(() => this._isLoadingDetailsPage.set(false)))
    }
    
    getPointDetails(id: string): Observable<any> {
      const url = `${this.rest.apiUrlBase}/api/get-point-app-details/${id}`;
      return this.http.get<any>(url).pipe(
        tap((response: any) => {
          if (response.error)
            throw new HttpErrorResponse({error: response.error, status: 404, statusText: 'Not Found'});
        })
      );
    }
      
    setPoint(item: any){
      this.itemDetails.set(item);
      this.stats.addPoint(item.id);
      this.increasePointVisits(item.id);
    }

    /**
     * Seeds the hiking cache and GPX signal from a stored offline record so the
     * standalone GPX route view (app-details-gpx-route) resolves with no network.
     * Only the minimal data the view needs is primed: the route name (title) and
     * the GPX text. Related-items map data is intentionally absent offline.
     */
    primeOfflineHiking(id: string | number, name: unknown, gpx: string | null) {
      this.hikingsData[String(id)] = { id: Number(id), name, type: 'hiking' };
      this.gpxData.set(gpx);
    }

    getHikingDetailsServiceSignal(id: string) {
      if (this.hikingsData?.[id]){
        this.debugService.info('Loading hiking cache data...', '[DetailesService]');
        return of(this.hikingsData[id]);
      }

      this._isLoadingDetailsPage.set(true);
      return this.getHikingDetails(id)
        .pipe(
          map((response: any) => {
            this.debugService.debug(response, '[DetailesService] Hiking API');
            return detailsAdapter(response)
          }),
          switchMap((details) =>  {
            const gpxUrl = details?.routeData?.gpx_file?.[this._translationService.selectedLanguage()]?.[0]?.src;
            if (!gpxUrl) return of(details);
  
            return  this.loadGpxFromUrl(gpxUrl).pipe(
              tap((gpx: any) => this.gpxData.set(gpx)),
              map(() => details),
              catchError((err) => {
                console.warn('GPX failed, continuing without it', err);
                return of(details);
              })
            )
          }),
          tap((response: any) => {
            this.debugService.debug(response, '[DetailesService] Hiking API Mapped');
            this.hikingsData[id] = response;
          }),
          finalize(() => this._isLoadingDetailsPage.set(false)))
    }

    getHikingDetails(id: string): Observable<any> {
      const url = `${this.rest.apiUrlBase}/api/get-hiking-app-details/${id}`;
      return this.http.get<any>(url).pipe(
        tap((response: any) => {
          if (response.error)
            throw new HttpErrorResponse({error: response.error, status: 404, statusText: 'Not Found'});
        })
      );
    }

    setHiking(item: any){
      this.itemDetails.set(item);
      this.stats.addHiking(item.id);
      this.increaseHikingVisits(item.id);
    }
    
    increasePointVisits(id: any){
      this.rest.increaseVisitsPoint(id).subscribe((response) => {
        const appHomePoint = this.appData?.dataHome?.homePoints
          ?.find((point: any) => point?.items?.find((p: any) => p.id == id))?.items
          ?.find((e: any) => e.id == id);
        if (appHomePoint) 
          appHomePoint.num_clicks++;

        try{
          const categoriesPoint = Object.keys(this.appData?.categoriesPoints);
          categoriesPoint.forEach(key => {
            const foundPoint = this.appData?.categoriesPoints?.[key]?.items?.find((e: any) => e.id == id && e.type === 'point');
            if (foundPoint)
              foundPoint.num_clicks++;
          });
        }catch(e){}

        const distributorHomePoint = this.appData?.dataHomeDistributor?.data?.homePoints
          ?.find((point: any) => point?.items?.find((p: any) => p.id == id))?.items
          ?.find((e: any) => e.id == id && e.type === 'point');
        if (distributorHomePoint) 
          distributorHomePoint.num_clicks++;
        
      });
    }

    increaseEventsVisits(id: any){
      this.rest.increaseVisitsEvent(id).subscribe((response) => {
        const appHomeEvent = this.appData?.dataHome?.events?.find((e: any) => e.id == id);
        if (appHomeEvent) 
          appHomeEvent.num_clicks++;

        const categoriesEvent = this.appData?.categoryEvents?.items?.find((e: any) => e.id == id && e.type === 'event');
        if (categoriesEvent)
          categoriesEvent.num_clicks++;

        const distributorHomeEvent = this.appData?.dataHomeDistributor?.data?.homePoints
          ?.find((point: any) => point?.items?.find((p: any) => p.id == id))?.items
          ?.find((e: any) => e.id == id && e.type === 'event');
        if (distributorHomeEvent) 
          distributorHomeEvent.num_clicks++;
        
      });
    }
    
    increaseHikingVisits(id: any){
      this.rest.increaseVisitsHiking(id).subscribe((response) => {
        const appHomeHiking = this.appData?.dataHome?.hikingList?.find((h: any) => h.id == id);
        if (appHomeHiking) 
          appHomeHiking.num_clicks++;

        const categoriesHiking = this.appData?.categoryHikings?.items?.find((e: any) => e.id == id && e.type === 'hiking');
        if (categoriesHiking)
          categoriesHiking.num_clicks++;

        const distributorHomeHiking = this.appData?.dataHomeDistributor?.data?.homePoints
          ?.find((point: any) => point?.items?.find((p: any) => p.id == id))?.items
          ?.find((e: any) => e.id == id && e.type === 'hiking');
        if (distributorHomeHiking) 
          distributorHomeHiking.num_clicks++;
      });
    }

  loadGpxFromUrl(url: string): Observable<string | null> {
    return this.http.get(url, { responseType: 'text' }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error("No se ha podido cargar archivo GPX");
        return of(null);
      })
    );
  }

  getPagePdf(pageUrl: string, fileName: string): Observable<Blob> {
    const endpoint = environment.pdfExportCCMM.enabled
      ? `${environment.pdfExportCCMM.backendUrl}?url=${encodeURIComponent(pageUrl)}&filename=${encodeURIComponent(fileName.replace(/\.pdf$/i, ''))}`
      : `${environment.pdfServiceUrl}?url=${encodeURIComponent(pageUrl)}`;
    return this.http.get(endpoint, { responseType: 'blob', observe: 'response' }).pipe(
      map((response: HttpResponse<Blob>) => {
        const body = response.body;
        const type = body?.type ?? '';
        if (!body || body.size === 0 || !type.includes('pdf')) {
          throw new Error(`Invalid PDF response (size=${body?.size ?? 0}, type=${type})`);
        }
        return body;
      })
    );
  }
}
